import React from 'react';
import { Ellipse, G, Line, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Q = V × A — duct in 3D perspective with parabolic velocity profile and dimensioned cross-section. */
export function DiagramVolumetricFlowRate() {
  // Duct geometry
  const x = 50, y = 90, w = 230, h = 80;
  const cy = y + h / 2;

  // Parabolic velocity streamlines — short near walls, long in core
  const streamlines = Array.from({ length: 9 }, (_, i) => {
    const t = (i + 0.5) / 9; // 0..1 across duct height
    const yPos = y + 6 + t * (h - 12);
    // Parabolic profile: max at center, zero at walls
    const len = 70 * (1 - Math.pow((t - 0.5) * 2, 2));
    const sx = x + 60;
    return { yPos, len, sx };
  });

  return (
    <DiagramCanvas>
      {/* 3D back face (perspective) */}
      <Polygon
        points={`${x},${y} ${x + 18},${y - 14} ${x + w + 18},${y - 14} ${x + w},${y}`}
        fill={DT.fill} stroke={DT.stroke} strokeWidth="1"
      />
      <Polygon
        points={`${x + w},${y} ${x + w + 18},${y - 14} ${x + w + 18},${y + h - 14} ${x + w},${y + h}`}
        fill={DT.fill} stroke={DT.stroke} strokeWidth="1"
      />
      {/* Duct front face */}
      <Rect x={x} y={y} width={w} height={h} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.5" />

      {/* Cross-section ellipse (cut plane near right) */}
      <Line x1={x + w - 50} y1={y - 4} x2={x + w - 50} y2={y + h + 4}
        stroke={DT.dim} strokeWidth="1" strokeDasharray="3,3" />
      <Ellipse cx={x + w - 50} cy={cy} rx={6} ry={h / 2}
        fill="none" stroke={DT.varColor} strokeWidth="1.5" opacity={0.9} />

      {/* Velocity profile streamlines (parabolic) */}
      {streamlines.map((s, i) => (
        <G key={i}>
          <Line x1={s.sx} y1={s.yPos} x2={s.sx + s.len} y2={s.yPos}
            stroke={DT.arrow} strokeWidth="1.2" opacity={0.85} />
          <Polygon
            points={`${s.sx + s.len},${s.yPos} ${s.sx + s.len - 4},${s.yPos - 2.2} ${s.sx + s.len - 4},${s.yPos + 2.2}`}
            fill={DT.arrow} opacity={0.85}
          />
        </G>
      ))}

      {/* Profile envelope outline */}
      <Polygon
        points={streamlines.map(s => `${s.sx + s.len},${s.yPos}`).join(' ') +
                ' ' + `${x + 60},${y + h - 6} ${x + 60},${y + 6}`}
        fill="none" stroke={DT.varColor} strokeWidth="1" opacity={0.5} strokeDasharray="2,2"
      />

      {/* Dimension lines — height (left side) */}
      <Line x1={x - 14} y1={y} x2={x - 14} y2={y + h} stroke={DT.dim} strokeWidth="1" />
      <Line x1={x - 18} y1={y} x2={x - 10} y2={y} stroke={DT.dim} strokeWidth="1" />
      <Line x1={x - 18} y1={y + h} x2={x - 10} y2={y + h} stroke={DT.dim} strokeWidth="1" />
      <SvgText x={x - 22} y={cy + 3} fill={DT.dim} fontSize="9" fontFamily={DT.mono} textAnchor="end">H</SvgText>

      {/* Dimension lines — width (top) */}
      <Line x1={x} y1={y - 24} x2={x + w} y2={y - 24} stroke={DT.dim} strokeWidth="1" />
      <Line x1={x} y1={y - 28} x2={x} y2={y - 20} stroke={DT.dim} strokeWidth="1" />
      <Line x1={x + w} y1={y - 28} x2={x + w} y2={y - 20} stroke={DT.dim} strokeWidth="1" />
      <SvgText x={x + w / 2} y={y - 28} fill={DT.dim} fontSize="9" fontFamily={DT.mono} textAnchor="middle">L (duct)</SvgText>

      {/* Cross-section label */}
      <SvgText x={x + w - 50} y={y + h + 16} fill={DT.dim} fontSize="9" fontFamily={DT.mono} textAnchor="middle">
        cross-section
      </SvgText>

      {/* Labels */}
      <LeaderLine x1={x + 95} y1={cy} x2={x + 30} y2={210} varName="V" desc="Velocity (FPM)" anchor="start" />
      <LeaderLine x1={x + w - 50} y1={y + 8} x2={x + w + 30} y2={210} varName="A" desc="Area (ft²)" anchor="start" />
      <LeaderLine x1={x + 175} y1={y + h - 8} x2={x + 165} y2={50} varName="Q" desc="Volumetric flow (CFM)" anchor="middle" />

      <FormulaTag formula="Q = V × A   (CFM = FPM × ft²)" />
    </DiagramCanvas>
  );
}
